import React, { useContext } from 'react';
import { Progress } from 'antd';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import { default_design } from '../../static/default_design';
import { Txt } from '../UiKit/Txt';
import { getCrossType } from '../utilsDesign';
import {
  getAuthorityCompatibility,
  getProfileCompatibility,
  getTypeCompatibility,
} from '../utilsHdCompat';
import { TagCloud } from '../Edu/Tags';
import { Avatar } from '../UiKit/Avatar';
import { AvatarName } from '../UiKit/AvatarName';
import { TooltipH } from '../UiKit/TooltipH';
import { Icon } from '../UiKit/Icon';

const TAG_SECTIONS = ['type', 'profile', 'authority'];
const SCORED_SECTIONS = ['type', 'profile'];

const PROGRESS_COLORS = {
  match: '#3fb56b',
  mismatch: '#cf5b5b',
  neutral: '#d9a441',
};

const getTagCompat = (section, designA, designB) => {
  if (section === 'type') {
    return getTypeCompatibility(designA?.type, designB?.type);
  }
  if (section === 'profile') {
    return getProfileCompatibility(designA?.profile, designB?.profile);
  }
  if (section === 'authority') {
    return getAuthorityCompatibility(designA?.auth, designB?.auth);
  }
  return null;
};

const getCompatLabelKey = (section, result) => {
  if (!result) return null;
  if (result.same) return `tags_compat_${section}_same`;
  if (result.status === 'match') return `tags_compat_${section}_good`;
  if (result.status === 'mismatch') return `tags_compat_${section}_hard`;
  return `tags_compat_${section}_diff`;
};

const TagsCompat = ({ section, designA, designB, $ui }) => {
  const result = getTagCompat(section, designA, designB);
  if (!result) return null;

  const labelKey = getCompatLabelKey(section, result);
  const detailLabel = $ui(labelKey);
  const showProgress = SCORED_SECTIONS.includes(section)
    && typeof result.score === 'number'
    && result.maxScore > 1;
  const scoreText = showProgress ? `${result.score}/${result.maxScore}` : null;
  const progressPercent = showProgress
    ? Math.round((result.score / result.maxScore) * 100)
    : null;

  return (
    <div className={`x_penta_compat ${result.status ? `x_penta_compat--${result.status}` : ''}`}>
      <span className="x_penta_compat_line x_penta_compat_line--1"></span>
      <div className={`x_penta_compat_inner x_penta_compat_inner--${result.status}`}>
        {showProgress ? (
          <div className="x_penta_compat_progress">
            <span className="x_penta_compat_label">
              <span className="x_penta_compat_pair">{detailLabel}</span>
              <span className="x_penta_compat_score_wrapper">
                {scoreText ? <span className="x_penta_compat_score">{scoreText}</span> : null}
                &nbsp;&nbsp;
                <Progress
                  percent={progressPercent}
                  steps={result.maxScore}
                  size="small"
                  showInfo={false}
                  strokeColor={PROGRESS_COLORS[result.status] || PROGRESS_COLORS.neutral}
                />
              </span>
            </span>
          </div>
        ) : (
          <span className="x_penta_compat_label">
            <span className="x_penta_compat_pair">{detailLabel}</span>
          </span>
        )}
      </div>
      <span className="x_penta_compat_line x_penta_compat_line--2"></span>
    </div>
  );
};

const TagSection = ({ section, design }) => {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;
  const $profile = context.$profile;
  const $type = context.$type;
  const $auths = context.$auths;

  const profileId = `${design['profile'][0]}${design['profile'][1]}`;

  if (section === 'type') {
    return (<>



      <div className="_fv _fgap04 _f100">
        <div className="_f">
          <div className="x_edu_content_label">
            <div>{$ui('basic_label_type')}</div>
          </div>
        </div>
        <div className="x_edu_content_title x_edu_content_title--sticky">
          <div className="x_edu_content_h x_edu_content_h--2">
            <Txt noP={true}>{$type(design['type'], 'title_full')}</Txt>
          </div>
        </div>
        {Boolean($type(design['type'], 'tags')) && <TagCloud label={<Icon>label</Icon>} flat={true} tagline={$type(design['type'], 'tags')} />}

        <div className="x_item_desc">
          <Txt>{$type(design['type'], 'description')}</Txt>
        </div>

      </div>
    </>
    );
  }

  if (section === 'profile') {
    return (<>



      <div className="_fv _fgap2 _f100">
        <div className="_fv _fgap04 _f100">
          <div className="_f">
            <div className="x_edu_content_label">
              <div>{$ui('basic_label_profile')}</div>
            </div>
          </div>
          <div className="_fv">
            <h1 className="x_edu_content_h x_edu_content_h--3 x_edu_content_h--profile">
              <span className="x_edu_content_h_italic">{$profile(profileId, 'profile')}</span> "{$profile(profileId, 'title')}"
            </h1>

            {Boolean($profile(profileId, 'tags')) && <TagCloud label={<Icon>label</Icon>} flat={true} tagline={$profile(profileId, 'tags')} />}

            <div className="x_item_desc">
              <Txt>{$profile(profileId, 'description')}</Txt>
            </div>

            <TooltipH title={<>
              <div className="_fv _fgap02">
                <div className="x_edu_content_h x_edu_content_h--3">
                  {getCrossType(design.profile) === 'right' && <>{$profile(profileId, 'profile')} — {$ui('profile_personal_title')}</>}
                  {getCrossType(design.profile) === 'left' && <>{$profile(profileId, 'profile')} — {$ui('profile_transpersonal_title')}</>}
                  {getCrossType(design.profile) === 'juxtaposition' && <>{$profile(profileId, 'profile')} — {$ui('profile_juxtaposition_title')}</>}
                </div>
                <div className="x_item_desc">
                  {getCrossType(design.profile) === 'right' && <Txt>{$ui('profile_personal_title', 'rich')}</Txt>}
                  {getCrossType(design.profile) === 'left' && <Txt>{$ui('profile_transpersonal_title', 'rich')}</Txt>}
                  {getCrossType(design.profile) === 'juxtaposition' && <Txt>{$ui('profile_juxtaposition_title', 'rich')}</Txt>}
                </div>
              </div>
            </>}>
              <strong>
                {getCrossType(design.profile) === 'right' && <>{$ui('profile_personal')}</>}
                {getCrossType(design.profile) === 'left' && <>{$ui('profile_transpersonal')}</>}
                {getCrossType(design.profile) === 'juxtaposition' && <>{$ui('profile_juxtaposition')}</>} <Icon>menu_book</Icon>
              </strong>
            </TooltipH>


          </div>



        </div>

      </div>
    </>
    );
  }

  return (<>

    <div className="_fv _fgap04 _f100">
      <div className="_f">
        <div className="x_edu_content_label">
          {$ui('basic_label_authority')}
        </div>
      </div>


      <div className=" _fv _f100">
        <h1 className="x_edu_content_h x_edu_content_h--3">
          <Txt>
            {$auths(design['auth'], 'title')}
          </Txt>
        </h1>

        {Boolean($auths(design['auth'], 'tags')) && <TagCloud label={<Icon>label</Icon>} flat={true} tagline={$auths(design['auth'], 'tags')} />}

        <div className="x_item_desc">
          <Txt>
            {$auths(design['auth'], 'description')}
          </Txt>
        </div>

      </div>
    </div>
  </>
  );
};

export const PentaTags = React.memo(({ penta }) => {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ui = context.$ui;

  const design1 = penta.members[0] ? penta.members[0].avatar.design : default_design;
  const design2 = penta.members[1] ? penta.members[1].avatar.design : default_design;
  const compareColumnWidth = `${(100 / penta.members.length)}%`;
  const showCompat = penta.members.length === 2;

  return (
    <>

      <div className="x_edu_content_title x_edu_content_title--group x_edu_content_title--sticky">
        <div className="x_edu_content_h x_edu_content_h--2">
          {$ui('penta_tags_title')}
        </div>
      </div>

      <div className="x_edu_content">
        <div className="x_edu_content_inner">


          <div className="_fv _fgap04 _f100">

            <div className="_fv _fgap1">
              {TAG_SECTIONS.map((section) => (
                <React.Fragment key={`tags_${penta.token}_${section}`}>
                  <div className="x_edu_penta_persons">
                    {penta.members.map((oneMember) => (
                      <div
                        className="x_edu_penta_oneperson _fgap1"
                        key={`${section}_${oneMember.avatar.token}`}
                        style={{ flexBasis: compareColumnWidth }}
                      >
                        <div className="x_phs_item1 x_phs_item1--100">
                          <TagSection section={section} design={oneMember.avatar.design} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {showCompat && (
                    <TagsCompat
                      section={section}
                      designA={design1}
                      designB={design2}
                      $ui={$ui}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
